import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Fuel as FuelIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const FUEL_LABEL = { diesel: "Diesel", petrol: "Petrol", adblue: "AdBlue" };
export const perLitre = (n) => `$${Number(n).toFixed(n >= 10 ? 2 : 3).replace(/0$/, "")}/L`;
const weekLabel = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" });

// Office sets one bowser price per fuel type per week (ex GST); bowser fills are costed from it.
export function WeeklyFuelPrice({ prices, onSaved }) {
  const [form, setForm] = useState({ fuel_type: "diesel", price_per_litre: "", week_of: prices?.this_week || "" });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!form.price_per_litre) { toast.error("Enter the price per litre"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/fuel/prices", { ...form, week_of: form.week_of || null });
      toast.success(data.priced_count ? `Price saved — ${data.priced_count} bowser fill-up${data.priced_count > 1 ? "s" : ""} costed` : "Price saved");
      setForm((f) => ({ ...f, price_per_litre: "" }));
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the price");
    } finally { setBusy(false); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Remove the ${FUEL_LABEL[p.fuel_type]} price for the week of ${weekLabel(p.week_of)}? Fill-ups costed from it will be re-costed from the previous price.`)) return;
    try { await api.delete(`/fuel/prices/${p.id}`); toast.success("Price removed"); onSaved(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not remove the price"); }
  };
  const current = prices?.current || {};
  return (
    <section className="mb-6 border border-zinc-300 dark:border-white/10 bg-white dark:bg-[#101620] p-4" data-testid="fuel-weekly-price">
      <div className="flex items-center gap-2 mb-1"><FuelIcon className="w-4 h-4 text-[#FF5F15]" /><h2 className="font-bold text-sm uppercase tracking-wider">Weekly bowser fuel price</h2></div>
      <p className="text-xs text-zinc-500 mb-3">Prices are per litre, excluding GST. Fill-ups from a yard or mobile bowser are costed automatically using the price for their week — including ones already logged without a cost.</p>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="fuel-current-prices">
        {Object.keys(FUEL_LABEL).map((ft) => (
          <div key={ft} className="px-3 py-2 rounded-sm border border-zinc-200 dark:border-white/10 min-w-[140px]" data-testid={`fuel-current-${ft}`}>
            <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">{FUEL_LABEL[ft]} · this week</div>
            <div className="font-mono font-bold text-lg">{current[ft] ? perLitre(current[ft].price_per_litre) : "Not set"}</div>
            {current[ft] && <div className="text-[10px] text-zinc-500">from week of {weekLabel(current[ft].week_of)}</div>}
          </div>
        ))}
      </div>
      <form onSubmit={save} className="grid grid-cols-2 md:grid-cols-[150px_150px_170px_auto] gap-3 items-end">
        <div>
          <Label className="label-tag">Fuel</Label>
          <Select value={form.fuel_type} onValueChange={(v) => setForm((f) => ({ ...f, fuel_type: v }))}>
            <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="fuel-price-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(FUEL_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-tag">$ per litre (ex GST)</Label>
          <Input type="number" step="0.001" min="0" inputMode="decimal" value={form.price_per_litre} onChange={(e) => setForm((f) => ({ ...f, price_per_litre: e.target.value }))}
            placeholder="e.g. 1.95" className="mt-1.5 rounded-sm border-zinc-400" data-testid="fuel-price-input" />
        </div>
        <div>
          <Label className="label-tag">Week starting</Label>
          <Input type="date" value={form.week_of} onChange={(e) => setForm((f) => ({ ...f, week_of: e.target.value }))} className="mt-1.5 rounded-sm border-zinc-400" data-testid="fuel-price-week-input" />
        </div>
        <Button type="submit" disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="fuel-price-save">{busy ? "Saving…" : "Save price"}</Button>
      </form>
      {prices?.history?.length > 0 && (
        <details className="mt-4 text-xs" data-testid="fuel-price-history">
          <summary className="cursor-pointer font-bold text-zinc-600 dark:text-zinc-300">Price history ({prices.history.length})</summary>
          <ul className="mt-2 divide-y divide-zinc-200 dark:divide-white/10">
            {prices.history.map((p) => (
              <li key={p.id} className="flex items-center gap-3 py-1.5">
                <span className="w-28 text-zinc-500">Week of {weekLabel(p.week_of)}</span>
                <span className="w-16 font-bold">{FUEL_LABEL[p.fuel_type]}</span>
                <span className="font-mono">{perLitre(p.price_per_litre)}</span>
                <span className="text-zinc-500 truncate">· {p.set_by}</span>
                <button type="button" onClick={() => remove(p)} className="ml-auto p-1 text-zinc-400 hover:text-red-600" aria-label="Remove price" data-testid={`fuel-price-remove-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
